import React, { useEffect, useRef } from 'react';

import { FLAG_STATE, webSocketWatcherEventTarget } from '@/components/UI/WebSocketWatcher';
import { useIdleContext } from '@/renderer/utils/useIdle';

// Use a random timer 250-1750ms to avoid hammering the server all at once
const FLAG_TIMEOUT = Math.floor(250 + Math.random() * 1500);

/**
 * Records the web-delivery state of a single rendered release receipt so the
 * server unread count drains as the member reads the feed. Mounted once per
 * receipt in the Release Center feed. It dispatches `messageReceipt/flag` events
 * that WebSocketWatcher coalesces into the bulk `POST /me/flag` write:
 *   • present (release center on screen + focused) → VISITED (7): read, drains
 *     the unread count.
 *   • backgrounded (tab hidden / window unfocused) → ACK (4): received but not
 *     read; it re-checks on a backoff and flips to VISITED the moment the member
 *     returns, without needing a mouse move.
 *
 * "Present" is keyed on visibility + focus, NOT the broad idle timer — see the
 * inline note. Exported (not inlined in MessageReceiptList) so the present-vs-
 * backgrounded read-ack decision has a focused unit test.
 */
export const MessageReceiptLogger = React.memo(
  function MessageReceiptInner(messageReceipt: API.MessageReceipt) {
    const { id, web } = messageReceipt;
    const webStatus = useRef(web);

    useEffect(() => {
      webStatus.current = web;
    }, [web]);

    const idle = useIdleContext();
    // "Present" = `idle.present` (release center on screen AND focused),
    // computed once in useIdle.tsx (#2072) so this can't drift from the sidebar
    // unread counter, the desktop-notification gate, or any other present-vs-
    // inactive consumer. Deliberately keyed on visibility+focus, NOT on the
    // broad idle timer (`idle.inactive`, which is `idle || !visible ||
    // !focused`): a member watching a live blast without moving the mouse trips
    // the idle timer (~10s desktop / ~25s mobile) while the feed is very much
    // on screen and being read. Marking a receipt READ must follow the SAME
    // "present, not merely non-idle" rule the sidebar unread counter uses
    // (WebSocketUnreadCounter `backgroundedRef`, #1885); otherwise a release
    // that arrives while the member is present-but-idle was recorded only as
    // ACK (< VISITED), so the server unread count never drained and the PWA/OS
    // push badge (and the sidebar badge reconciled from it) kept climbing while
    // they actively watched (#1212 badge sum; the #1885 on-page guard suppresses
    // the LOCAL increment but the server count still has to drain to hold at 0).
    const isPresent = useRef(idle.present);

    useEffect(() => {
      isPresent.current = idle.present;
    }, [idle.present]);

    useEffect(() => {
      let newState = webStatus.current;
      let timer: ReturnType<typeof setTimeout> | null | undefined;
      let fn: null | undefined | (() => void);
      let nextTimeout = 15000 + Math.random() * 5000;

      if (newState < FLAG_STATE.VISITED) {
        // Mark it read (VISITED) while the member is present, or merely
        // acknowledged (ACK) while backgrounded — re-checking presence on each
        // pass so a receipt that arrived while the tab was hidden flips to VISITED
        // as soon as they return, without needing a mouse move.
        fn = () => {
          timer = null;
          const present = isPresent.current;

          if (
            newState <= FLAG_STATE.ACK && // not yet read
            !present
          ) {
            if (newState < FLAG_STATE.ACK) {
              webSocketWatcherEventTarget.dispatchEvent(
                new CustomEvent('ras:webSocketWatcher/flag', {
                  detail: {
                    event: `messageReceipt/flag`,
                    data: {
                      id: id,
                      item: messageReceipt,
                      flag: FLAG_STATE.VISITED,
                      idleFlag: FLAG_STATE.ACK,
                    },
                  },
                }),
              );

              webStatus.current = newState = FLAG_STATE.ACK;
            }
          } else if (newState < FLAG_STATE.VISITED) {
            // not yet read
            if (present) {
              fn = null;

              webSocketWatcherEventTarget.dispatchEvent(
                new CustomEvent('ras:webSocketWatcher/flag', {
                  detail: {
                    event: `messageReceipt/flag`,
                    data: {
                      id: id,
                      item: messageReceipt,
                      flag: FLAG_STATE.VISITED,
                    },
                  },
                }),
              );

              webStatus.current = newState = FLAG_STATE.VISITED;

              return; // return to avoid re-running read timer
            }
          }

          // Keep running the timer until they come back, so we can later flag it as VISITED
          if (fn) {
            timer = setTimeout(fn, nextTimeout); // second check is faster
            nextTimeout = 60000 + Math.random() * 30000; // third+ check is long
          }
        };

        timer = setTimeout(fn, FLAG_TIMEOUT);
      }

      if (timer && fn) {
        return () => {
          if (timer) {
            clearTimeout(timer);
            timer = null;

            if (fn) {
              fn(); // flag immediately on navigate away
              fn = null;
            }
          }
        };
      }
    }, []);

    return null;
  },
  (prev, next) => prev?.id === next?.id,
);

export default MessageReceiptLogger;
